import type { ChangeEvent } from 'react';
import { FormField, NewDateInput, Paragraph } from '@george-labs.com/design-system';
import type { DateInputPropTypes } from '@george-labs.com/design-system/types/components/Input/NewDateInput/DateInputPropTypes';

import { useField } from '@luna/form2';
import { DateFormat, formatDate, isValidDate } from '@luna/date';

import type { FormFieldProps } from './types/FormFieldProps';
import { shouldShowError } from './FieldErrorVisibilityService';

interface Props extends FormFieldProps {
    inputProps?: Partial<DateInputPropTypes>;
    isErrorVisible?: boolean;
    shouldClearOnUnmount?: boolean;
    nullable?: boolean;
}

type FieldValue = string | undefined | null;

const LABEL_COLUMN_WIDTH = 4;

const getDate = (date: FieldValue): Date | undefined => {
    if (!date) {
        return undefined;
    }
    return new Date(date);
};

const getFormValue = (date: Date | null, nullable?: boolean): string | null => {
    if (!date) {
        return nullable ? null : '';
    }
    if (!isValidDate(date)) {
        return '';
    }
    return formatDate(date, DateFormat.ISO_DATE_FORMAT);
};

export const FormDateField = ({
    className,
    label,
    'data-cy': dataCy,
    labelColumnWidth = LABEL_COLUMN_WIDTH,
    fieldName,
    required,
    inputProps,
    warning,
    info,
    alert,
    labelPosition,
    description,
    placeholder,
    isErrorVisible = true,
    addon,
    shouldClearOnUnmount,
    disabled,
    nullable,
    showErrorBeforeBlur,
}: Props) => {
    const [fieldProps, { touched, error, dirty }] = useField<FieldValue>(fieldName, {
        shouldClearOnUnmount,
    });

    const isFieldErrorVisible = shouldShowError(
        isErrorVisible,
        touched,
        error,
        showErrorBeforeBlur,
        dirty,
    );

    const blurField = () => fieldProps.onBlur();

    const { onDateChange, ...restInputProps } = inputProps ?? {};

    return (
        <FormField
            className={className}
            required={required}
            description={description}
            disabled={disabled}
            input={
                <NewDateInput
                    name={fieldProps.name}
                    onClose={fieldProps.onBlur}
                    onBlur={blurField}
                    date={getDate(fieldProps.value)}
                    onDateChange={(date: Date | null) => {
                        fieldProps.onChange({
                            target: {
                                value: getFormValue(date, nullable),
                            },
                        } as ChangeEvent<HTMLInputElement>);
                        onDateChange?.(date);
                    }}
                    data-cy={dataCy}
                    {...restInputProps}
                />
            }
            labelPosition={labelPosition}
            label={label}
            placeholder={placeholder}
            error={
                isFieldErrorVisible && <Paragraph data-cy={`${dataCy}-error`}>{error}</Paragraph>
            }
            labelColumnWidth={labelColumnWidth}
            warning={warning}
            info={info}
            alert={alert}
            addon={addon}
        />
    );
};

FormDateField.LABEL_POSITION = FormField.LABEL_POSITION;
